import type { ReactNode } from 'react';
import { CalendarRange, CircleDollarSign, FileSpreadsheet } from 'lucide-react';
import { Image } from '@/components/ui/Image';

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

const benefits = [
  { label: 'Timeline planning', icon: CalendarRange },
  { label: 'Automatic billable totals', icon: CircleDollarSign },
  { label: 'Clean exports', icon: FileSpreadsheet },
];

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <main
      data-testid="auth-layout"
      className="min-h-screen overflow-x-hidden bg-background lg:grid lg:grid-cols-[minmax(0,1.12fr)_minmax(440px,0.88fr)]"
    >
      <section
        data-testid="auth-brand-panel"
        className="relative min-h-[176px] overflow-hidden bg-[linear-gradient(135deg,#0F172A_0%,#0B2A66_48%,#2563EB_100%)] text-white lg:min-h-screen"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.32) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_82%_22%,rgba(59,130,246,0.45),transparent_34%),radial-gradient(circle_at_18%_86%,rgba(15,23,42,0.65),transparent_42%)]"
        />

        <div className="relative mx-auto flex h-full max-w-[900px] flex-col px-6 py-6 sm:px-10 lg:min-h-screen lg:px-12 lg:py-10 xl:px-16 xl:py-12">
          <div className="flex items-center gap-3 text-lg font-semibold tracking-tight sm:text-xl">
            <Image
              src="/logo-icon.png"
              alt=""
              aria-hidden="true"
              width={48}
              height={48}
              priority
              className="h-12 w-12 rounded-xl shadow-lg shadow-black/20"
            />
            <span>Day Sheetly</span>
          </div>

          <div className="mt-6 max-w-2xl lg:mt-10 xl:mt-12">
            <h2 className="text-2xl font-semibold leading-tight tracking-tight sm:text-3xl lg:text-4xl xl:text-5xl">
              Time tracking built for freelancers
            </h2>
            <p className="mt-4 hidden max-w-xl text-base leading-7 text-blue-100 lg:block xl:text-lg">
              Track hours, calculate billable amounts, and export clear reports — without the
              clutter.
            </p>
          </div>

          <div className="mt-7 hidden flex-wrap gap-x-8 gap-y-4 lg:flex xl:mt-9">
            {benefits.map(({ label, icon: Icon }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm font-medium text-blue-50">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-blue-300/50 bg-blue-400/10">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                {label}
              </div>
            ))}
          </div>

          <div
            data-testid="auth-product-preview"
            className="mt-8 hidden overflow-hidden rounded-2xl border border-white/25 bg-white shadow-[0_30px_80px_-30px_rgba(2,6,23,0.9)] lg:block xl:mt-10"
          >
            <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="ml-3 h-5 flex-1 rounded-md bg-white shadow-inner" />
            </div>
            <picture>
              <source
                data-testid="auth-product-source"
                media="(min-width: 1024px)"
                srcSet="/auth-timeline-preview.png"
              />
              <img
                src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
                alt="Day Sheetly timeline showing project work across a weekly schedule"
                width={1600}
                height={1000}
                loading="lazy"
                decoding="async"
                className="aspect-[16/9] w-full object-cover object-top"
              />
            </picture>
          </div>
        </div>
      </section>

      <section className="flex min-h-[calc(100vh-176px)] items-center justify-center bg-background px-5 py-10 sm:px-8 lg:min-h-screen lg:px-10 lg:py-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-10 hidden items-center gap-3 lg:flex">
            <Image
              src="/logo-icon.png"
              alt=""
              aria-hidden="true"
              width={44}
              height={44}
              priority
              className="h-11 w-11 rounded-xl"
            />
            <span className="text-xl font-semibold tracking-tight text-foreground">Day Sheetly</span>
          </div>

          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
            <p className="text-base text-muted-foreground">{subtitle}</p>
          </header>

          {children}

          <nav className="mt-8 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground" aria-label="Legal">
            <a href="https://daysheetly.com/privacy" className="hover:text-foreground hover:underline">
              Privacy policy
            </a>
            <a href="https://daysheetly.com/terms" className="hover:text-foreground hover:underline">
              Terms of service
            </a>
            <a href="https://daysheetly.com/delete-account" className="hover:text-foreground hover:underline">
              Delete account
            </a>
          </nav>
        </div>
      </section>
    </main>
  );
}
